import type { QuoteRequestStatus } from '@repo/api-contracts';
import { type ReactElement, useId, useState } from 'react';
import { Link } from 'react-router';
import { allowedQuoteActions } from '@/entities/quote';
import { env, myOrderDetailPath, orderPlacePath } from '@/shared/config';
import { Button, buttonVariants, cn } from '@/shared/ui';
import { useCancelQuoteRequest } from '../model/use-cancel-quote-request';
import { DeclineQuoteForm } from './decline-quote-form';
import { PrintQuoteButton } from './print-quote-button';

export interface QuoteActionButtonsProps {
  readonly quoteRequestId: string;
  readonly status: QuoteRequestStatus;
  /** 살아 있는 주문 id(주문됨일 때 "주문 보기" 대상). 없으면 링크를 만들지 않는다. */
  readonly orderId: string | null;
  /** 수정 요청 버튼 — 위젯이 사유 폼을 연다. */
  readonly onRequestRevision: () => void;
  /** "수정 요청" 버튼의 id — 위젯이 사유 폼을 닫은 뒤 이 버튼으로 포커스를 돌려준다. */
  readonly revisionButtonId?: string;
}

/** 보조 버튼 — 상품 상세 CTA 아래 줄처럼 같은 폭으로 나눠 선다. */
const SECONDARY_CLASS = 'h-12 min-w-28 flex-1 border-foreground/20 text-sm';
/** 되돌릴 수 없는 버튼 — 빨간 면 대신 빨간 글자·테두리로 덜 소란하게 둔다. */
const DESTRUCTIVE_OUTLINE_CLASS =
  'border-destructive/40 text-destructive hover:bg-destructive/5 hover:text-destructive';

const CANCEL_CONFIRM = '견적 요청을 취소하시겠습니까? 취소한 뒤에는 되돌릴 수 없습니다.';

/**
 * 상태별 응답 버튼(견적 설계 §7-3). 주문하기는 gate(`VITE_ORDER_PLACEMENT_ENABLED`, 기본 닫힘 — R17 사용자 확인 전)가 열려
 * 있을 때만 주문하기 화면 링크이고, 닫혀 있으면 비활성 + "주문 기능 준비 중"(aria-describedby 로 연결)이다.
 * 거절은 선택 사유를 받는 확인 단계를, 요청 취소는 확인 창을 거친다. 주문됨은 내 주문 상세로 가는 "주문 보기"와 인쇄만
 * 남긴다(주문 설계 §8-4) — 결제하기·주문 취소·영수증은 내 주문 상세에 있다.
 */
export const QuoteActionButtons = ({
  quoteRequestId,
  status,
  orderId,
  onRequestRevision,
  revisionButtonId,
}: QuoteActionButtonsProps): ReactElement | null => {
  const actions = allowedQuoteActions(status);
  const cancellation = useCancelQuoteRequest();
  const [isDeclineOpen, setIsDeclineOpen] = useState(false);
  const orderHintId = useId();
  const declineButtonId = useId();

  if (actions.length === 0) {
    return null;
  }

  const handleCancel = (): void => {
    if (window.confirm(CANCEL_CONFIRM)) {
      void cancellation.cancel({ quoteRequestId });
    }
  };

  const closeDecline = (): void => {
    setIsDeclineOpen(false);
    // 확인 단계를 닫으면 그 단계를 연 "거절" 버튼으로 포커스를 돌려준다.
    document.getElementById(declineButtonId)?.focus();
  };

  const canViewOrder = actions.includes('VIEW_ORDER') && orderId !== null;
  const hasSecondary =
    actions.includes('REQUEST_REVISION') ||
    actions.includes('DECLINE') ||
    canViewOrder ||
    actions.includes('PRINT') ||
    actions.includes('CANCEL_REQUEST');

  return (
    <div className="flex flex-col gap-3 border-t border-foreground/10 pt-6">
      {actions.includes('ORDER') && env.VITE_ORDER_PLACEMENT_ENABLED ? (
        <Link
          to={orderPlacePath(quoteRequestId)}
          className={cn(buttonVariants({ size: 'lg' }), 'h-14 w-full text-base')}
        >
          주문하기
        </Link>
      ) : null}
      {actions.includes('ORDER') && !env.VITE_ORDER_PLACEMENT_ENABLED ? (
        <div className="flex flex-col gap-2">
          <Button
            type="button"
            size="lg"
            disabled
            aria-describedby={orderHintId}
            className="h-14 w-full text-base"
          >
            주문하기
          </Button>
          <span id={orderHintId} className="text-center text-xs text-muted-foreground">
            주문 기능 준비 중
          </span>
        </div>
      ) : null}
      {hasSecondary ? (
        <div className="flex flex-wrap gap-2">
          {actions.includes('REQUEST_REVISION') ? (
            <Button
              id={revisionButtonId}
              type="button"
              variant="outline"
              className={SECONDARY_CLASS}
              onClick={onRequestRevision}
            >
              수정 요청
            </Button>
          ) : null}
          {actions.includes('DECLINE') ? (
            <Button
              id={declineButtonId}
              type="button"
              variant="outline"
              className={cn(SECONDARY_CLASS, DESTRUCTIVE_OUTLINE_CLASS)}
              aria-expanded={isDeclineOpen}
              onClick={() => setIsDeclineOpen((open) => !open)}
            >
              거절
            </Button>
          ) : null}
          {canViewOrder ? (
            <Link
              to={myOrderDetailPath(orderId)}
              className={cn(buttonVariants({ variant: 'outline' }), SECONDARY_CLASS)}
            >
              주문 보기
            </Link>
          ) : null}
          {actions.includes('PRINT') ? <PrintQuoteButton className={SECONDARY_CLASS} /> : null}
          {actions.includes('CANCEL_REQUEST') ? (
            <Button
              type="button"
              variant="outline"
              className={cn(SECONDARY_CLASS, DESTRUCTIVE_OUTLINE_CLASS)}
              isLoading={cancellation.isPending}
              onClick={handleCancel}
            >
              요청 취소
            </Button>
          ) : null}
        </div>
      ) : null}
      {isDeclineOpen && actions.includes('DECLINE') ? (
        <DeclineQuoteForm quoteRequestId={quoteRequestId} onClose={closeDecline} />
      ) : null}
      {cancellation.error ? (
        <p role="alert" className="text-sm text-destructive">
          {cancellation.error}
        </p>
      ) : null}
    </div>
  );
};
